<template>
  <div class="rights-locations">
    <v-container>
      <v-row justify="center">
        <v-col class="d-flex justify-center">
          <v-btn color="success" @click="multipleLocationsDialog = true">
            <v-icon left>mdi-plus</v-icon>
            <span>Manage multiple</span>
          </v-btn>
        </v-col>
      </v-row>
      <RightsLocationsTab
        :organizationLocations="organizationLocations"
        :locations="locations"
        :nestedLocations="nestedLocations"
        :organizationLocationsLoading="organizationLocationsLoading"
        :locationsLoading="locationsLoading"
        v-on:openLocation="openLocation"
      />
    </v-container>
    <v-dialog v-model="locationDialog" max-width="900">
      <RightUsersDialog
        :title="title"
        :admins="selectedLocationUsers.admins"
        :editors="selectedLocationUsers.editors"
        :viewers="selectedLocationUsers.viewers"
        :organizationUsers="organizationUsers"
        :isLoading="organizationUsersLoading"
        v-on:cancel="locationDialog = false"
        v-on:addAdminUser="addAdminUser"
        v-on:addEditorUser="addEditorUser"
        v-on:addViewerUser="addViewerUser"
        v-on:removeOneAdmin="removeAdminUser"
        v-on:removeOneEditor="removeEditorUser"
        v-on:removeOneViewer="removeViewerUser"
      />
    </v-dialog>
    <v-dialog v-model="multipleLocationsDialog" max-width="900">
      <MultipleLocationsRights
        title="Manage users"
        :organizationUsers="organizationUsers"
        :locations="locations"
        v-on:add="addMultiple"
        v-on:remove="removeMultiple"
      />
    </v-dialog>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import mixin from "@/mixins";
import RightsLocationsTab from "@/components/Access/Rights/RightsLocationsTab";
import RightUsersDialog from "@/components/Access/Rights/RightUsersDialog";
import MultipleLocationsRights from "@/components/Access/Rights/MultipleLocationsRights";

export default {
  components: { RightsLocationsTab, RightUsersDialog, MultipleLocationsRights },
  mixins: [mixin],
  data() {
    return {
      title: "",
      nestedLocations: [],
      locations: [],
      errorMessage: "",
      snackbar: false,
      organizationLocations: [],
      organizationUuid: "",
      locationDialog: false,
      multipleLocationsDialog: false,
      selectedLocation: {},
      selectedLocationUsers: { admins: [], editors: [], viewers: [] },
    };
  },
  computed: {
    ...mapState({
      currentOrganization: (state) => state.organizations.current,
      organizationUsers: (state) => state.organizations.users,
      organizationUsersLoading: (state) =>
        state.organizations.fetchAllUsers.pending,
      locationsError: (state) => state.locations.error,
      organizationLocationsLoading: (state) =>
        state.organizations.fetchAllLocations.pending,
      locationsLoading: (state) => state.locations.fetchAll.pending,
    }),
  },
  watch: {
    currentOrganization() {
      this.refresh();
    },
  },
  methods: {
    ...mapActions("organizations", {
      fetchAllOrganizationLocations: "fetchAllLocations",
      fetchAllUsers: "fetchAllUsers",
      fetchOrganizationLocationUsers: "fetchOrganizationLocationUsers",
      addOrganizationLocationUser: "addOrganizationLocationUser",
      removeOrganizationLocationUser: "removeOrganizationLocationUser",
    }),
    ...mapActions("locations", { fetchAllLocations: "fetchAll" }),
    refresh() {
      this.fetchAllLocations({ nested: true })
        .then((locations) => {
          this.nestedLocations = locations;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllLocations({ nested: false })
        .then((locations) => {
          this.locations = locations;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllOrganizationLocations({
        organizationUuid: this.currentOrganization.uuid,
      })
        .then((locations) => {
          this.organizationLocations = locations;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllUsers({
        organizationUuid: this.currentOrganization.uuid,
      }).catch((error) => {
        this.snackbar = true;
        this.errorMessage = this.errorDisplay(error);
      });
    },
    openLocation(location) {
      this.fetchOrganizationLocationUsers({
        organizationUuid: this.currentOrganization.uuid,
        locationUuid: location.uuid,
      })
        .then((users) => {
          this.title =
            "Users: Location " +
            this.locations.find((loc) => loc.uuid === location.uuid).label;
          this.selectedLocationUsers = users;
          this.selectedLocation = location;
          this.locationDialog = true;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    refreshSelectedLocation() {
      this.fetchOrganizationLocationUsers({
        organizationUuid: this.currentOrganization.uuid,
        locationUuid: this.selectedLocation.uuid,
      })
        .then((users) => {
          this.selectedLocationUsers = users;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addUser(userUuid, role) {
      this.addOrganizationLocationUser({
        organizationUuid: this.currentOrganization.uuid,
        locationUuid: this.selectedLocation.uuid,
        userUuid: userUuid,
        role: role,
      })
        .then(() => {
          this.refreshSelectedLocation();
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeUser(userUuid, role) {
      this.removeOrganizationLocationUser({
        organizationUuid: this.currentOrganization.uuid,
        locationUuid: this.selectedLocation.uuid,
        userUuid: userUuid,
        role: role,
      })
        .then(() => {
          this.refreshSelectedLocation();
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addAdminUser(user) {
      this.addUser(user.uuid, "admins");
    },
    addEditorUser(user) {
      this.addUser(user.uuid, "editors");
    },
    addViewerUser(user) {
      this.addUser(user.uuid, "viewers");
    },
    removeAdminUser(user) {
      this.removeUser(user.uuid, "admins");
    },
    removeEditorUser(user) {
      this.removeUser(user.uuid, "editors");
    },
    removeViewerUser(user) {
      this.removeUser(user.uuid, "viewers");
    },
    addMultiple(users, locations, rights) {
      let promisesData = this.getPromisesData(users, locations, rights);
      let promises = [];
      for (let data of promisesData) {
        promises.push(this.addOrganizationLocationUser(data));
      }
      Promise.all(promises).then(() => {
        this.refresh();
      });
    },
    removeMultiple(users, locations, rights) {
      let promisesData = this.getPromisesData(users, locations, rights);
      let promises = [];
      for (let data of promisesData) {
        promises.push(this.removeOrganizationLocationUser(data));
      }
      Promise.all(promises).then(() => {
        this.refresh();
      });
    },
    getPromisesData(users, locations, rights) {
      let promisesData = [];
      for (let user of users) {
        for (let location of locations) {
          for (let right of rights) {
            promisesData.push({
              organizationUuid: this.currentOrganization.uuid,
              locationUuid: location.uuid,
              userUuid: user.uuid,
              role: right.value,
            });
          }
        }
      }
      return promisesData;
    },
  },
  created() {
    if (this.currentOrganization) {
      this.refresh();
    }
  },
};
</script>

<style>
</style>